"use client";

import { useState } from "react";
import { Bell } from "@/components/icons";
import type { Id } from "@convex/_generated/dataModel";
import type { AccountType } from "@/lib/auth/types";
import { Button } from "@/components/ui/button";
import { formatActionCount, useActivityFeed } from "@/components/activity/use-activity-feed";
import { ActivityPanel } from "@/components/activity/activity-panel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface NotificationBellProps {
  companyId?: Id<"companies">;
  accountTypes?: readonly AccountType[];
}

export function NotificationBell({
  companyId,
  accountTypes,
}: NotificationBellProps) {
  const { actionCount } = useActivityFeed(companyId, accountTypes);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="quiet"
          size="icon-compact"
          aria-label="Activity"
          title="Activity"
          className="relative rounded-md p-0 hover:bg-accent/60 touch-manipulation"
        >
          <Bell className="h-4 w-4" />
          {actionCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-action-fill px-1 text-micro font-semibold leading-none text-action-fill-foreground ring-2 ring-background">
              {formatActionCount(actionCount)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
      >
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-semibold">Activity</h2>
        </div>
        <div className="flex max-h-layout-md min-h-44 flex-col">
          <ActivityPanel
            companyId={companyId}
            accountTypes={accountTypes}
            onNavigate={() => setIsOpen(false)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
